import {
  useMutation,
  useQuery,
  useSubscription
} from '@apollo/client'
import { useEffect, useRef, useState } from 'react'

import useAuth from 'data/gradoo/hooks/useAuth'
import {
  CANCEL_AUTOMATION,
  PAUSE_AUTOMATION,
  RESUME_AUTOMATION
} from 'data/layoutcreator/mutations/automation'
import {
  GET_AUTOMATION_STATUS,
  GET_PROJECT_AUTOMATIONS
} from 'data/layoutcreator/queries/automation'
import { TRACK_AUTOMATION_STATUS } from 'data/layoutcreator/subscriptions/automation'
import { GET_PROJECT_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import { CoverTypes } from 'shared/types/global'
import Flex from 'shared/ui/Flex'
import Text from 'shared/ui/Text'

import { ReactComponent as PauseIcon } from './assets/pause.svg'
import { ReactComponent as PlayIcon } from './assets/play.svg'
import { ReactComponent as RestartIcon } from './assets/restart.svg'
import { ReactComponent as StopIcon } from './assets/stop-circle.svg'
import RestartModal from './components/RestartModal'
import {
  AutomationItemRow,
  ChevronButton,
  ChevronIcon,
  Container,
  Content,
  ControlButton,
  HistoryContainer,
  MiniStatus,
  SeeMetadataButton,
  Status,
  StatusContainer,
  StatusTitle, // StatusDot,
  // StatusText,
  Title
} from './styles'

const STATUS_LABELS: Record<string, (module?: string) => string> = {
  creatingProject: () => 'Creating project...',
  fetchingData: module =>
    module ? `Fetching ${module}...` : 'Fetching data...',
  sortingData: () => 'Sorting data...',
  planning: () => 'Planning pages...',
  executingPlan: () => 'Populating pages...',
  completed: () => 'Automation completed',
  paused: () => 'Automation paused',
  cancelled: () => 'Automation cancelled',
  failed: () => 'Automation failed'
}

export enum AutomationStatuses {
  creatingProject = 'creatingProject',
  fetchingData = 'fetchingData',
  sortingData = 'sortingData',
  planning = 'planning',
  executingPlan = 'executingPlan',
  completed = 'completed',
  paused = 'paused',
  cancelled = 'cancelled',
  failed = 'failed'
}

type AutomationError = {
  code: string
  message: string
  step: string
  detailsMessage: string
  fatal: boolean
  module: string
}

type AutomationHistoryItem = {
  automationId: string
  status: string
  entities: string[]
  createdAt: string | null
}

type AutomationStatusBarProps = {
  projectId: string
}

const AutomationStatusBar = ({
  projectId
}: AutomationStatusBarProps) => {
  const { authGroupId } = useAuth()

  const [isExpanded, setIsExpanded] = useState(false)
  const [currentAutomationId, setCurrentAutomationId] = useState<
    string | null
  >(null)
  const [isRestartModalOpen, setIsRestartModalOpen] = useState(false)
  const [errors, setErrors] = useState<AutomationError[]>([])

  const { data: projectMetaData } = useQuery(GET_PROJECT_META, {
    variables: {
      projectId
    }
  })
  const projectMeta = projectMetaData?.getProjectMeta

  const { data: statusData } = useQuery(GET_AUTOMATION_STATUS, {
    variables: {
      projectId
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (!data.getAutomationStatus) {
        setCurrentAutomationId(null)
        setErrors([])
        return
      }

      setCurrentAutomationId(data.getAutomationStatus.automationId)
      setErrors(data.getAutomationStatus.errors || [])
    }
  })

  const {
    data: projectAutomationsData,
    loading: isProjectAutomationsLoading,
    refetch: refetchProjectAutomations
  } = useQuery(GET_PROJECT_AUTOMATIONS, {
    variables: {
      projectId
    },
    fetchPolicy: 'network-only'
  })

  const { data: subscriptionData } = useSubscription(
    TRACK_AUTOMATION_STATUS,
    {
      variables: {
        projectId
      },
      shouldResubscribe: true,
      onData: ({ data }) => {
        const newErrors = data.data.trackAutomation.errors
        const newErrorCodes = newErrors.map(
          (error: AutomationError) => error.code
        )

        if (
          data.data.trackAutomation.automationId !==
          currentAutomationId
        ) {
          setCurrentAutomationId(
            data.data.trackAutomation.automationId
          )
          setErrors([])
        }
        setErrors([
          ...errors.filter(
            error => !newErrorCodes.includes(error.code)
          ),
          ...newErrors
        ])
      }
    }
  )

  const { refetch: refetchProjectDocuments } = useQuery(
    GET_PROJECT_DOCUMENTS,
    {
      variables: {
        groupId: authGroupId!,
        projectId: projectId
      }
    }
  )

  const [pauseAutomation] = useMutation(PAUSE_AUTOMATION)
  const [resumeAutomation] = useMutation(RESUME_AUTOMATION)
  const [cancelAutomation] = useMutation(CANCEL_AUTOMATION)

  const status =
    subscriptionData?.trackAutomation?.status ||
    statusData?.getAutomationStatus?.status
  const automationHistory: AutomationHistoryItem[] =
    projectAutomationsData?.getProjectAutomations?.automations || []

  useEffect(() => {
    if (!currentAutomationId) {
      return
    }

    refetchProjectAutomations()
  }, [currentAutomationId, refetchProjectAutomations])

  const currentFillingPageIndex = useRef(null)

  useEffect(() => {
    const index = subscriptionData?.trackAutomation?.fillingPageIndex
    if (index !== currentFillingPageIndex.current) {
      currentFillingPageIndex.current = index
      refetchProjectDocuments()
    }
  }, [
    subscriptionData?.trackAutomation?.fillingPageIndex,
    refetchProjectDocuments
  ])

  const controls: {
    Icon: React.FC<React.SVGProps<SVGSVGElement>>
    label: string
    action: () => void
    statuses?: AutomationStatuses[]
  }[] = [
    {
      Icon: PlayIcon,
      label: 'Play',
      action: () =>
        resumeAutomation({
          variables: {
            automationId:
              statusData?.getAutomationStatus?.automationId
          }
        }),
      statuses: [AutomationStatuses.paused]
    },
    {
      Icon: PauseIcon,
      label: 'Pause',
      action: () =>
        pauseAutomation({
          variables: {
            automationId:
              statusData?.getAutomationStatus?.automationId
          }
        }),
      statuses: [
        AutomationStatuses.creatingProject,
        AutomationStatuses.fetchingData,
        AutomationStatuses.sortingData,
        AutomationStatuses.planning,
        AutomationStatuses.executingPlan
      ]
    },
    {
      Icon: StopIcon,
      label: 'Cancel',
      action: () =>
        cancelAutomation({
          variables: {
            automationId:
              statusData?.getAutomationStatus?.automationId
          }
        }),
      statuses: [
        AutomationStatuses.creatingProject,
        AutomationStatuses.fetchingData,
        AutomationStatuses.sortingData,
        AutomationStatuses.planning,
        AutomationStatuses.executingPlan
      ]
    },
    {
      Icon: RestartIcon,
      label: 'Restart',
      action: () => setIsRestartModalOpen(true),
      statuses: [
        AutomationStatuses.completed,
        AutomationStatuses.failed,
        AutomationStatuses.cancelled
      ]
    }
  ]

  const formatStatus = (status?: string) => {
    if (!status) {
      return 'No automation status'
    }

    if (
      status === AutomationStatuses.completed &&
      errors.length > 0
    ) {
      return 'Automation completed with errors'
    }

    return STATUS_LABELS[status]?.(subscriptionData?.trackAutomation?.currentModule) || status
  }

  const formatHistoryDate = (value?: string | null) => {
    if (!value) {
      return 'Unknown date'
    }

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
      return 'Unknown date'
    }

    const locales = [
      typeof navigator !== 'undefined' ? navigator.language : '',
      'de-DE'
    ].filter(Boolean) as string[]

    const timeFormatter = new Intl.DateTimeFormat(locales, {
      hour: '2-digit',
      minute: '2-digit'
    })

    const now = new Date()
    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()

    if (isSameDay(date, now)) {
      return `Today, ${timeFormatter.format(date)}`
    }

    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)

    if (isSameDay(date, yesterday)) {
      return `Yesterday, ${timeFormatter.format(date)}`
    }

    const dateFormatter = new Intl.DateTimeFormat(locales, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })

    return dateFormatter.format(date)
  }

  const formatEntities = (entities: string[] = []) => {
    if (!entities.length) {
      return 'No entities'
    }

    return entities.join(', ')
  }

  const writeTextToDumpTab = ({
    dumpTab,
    title,
    text
  }: {
    dumpTab: Window
    title: string
    text: string
  }) => {
    dumpTab.document.open()
    dumpTab.document.write(
      '<!doctype html><html><head></head><body></body></html>'
    )
    dumpTab.document.close()
    dumpTab.document.title = title

    const pre = dumpTab.document.createElement('pre')
    pre.textContent = text
    pre.style.margin = '0'
    pre.style.padding = '16px'
    pre.style.whiteSpace = 'pre-wrap'
    pre.style.wordBreak = 'break-word'
    dumpTab.document.body.appendChild(pre)
  }

  const handleSeeDataClick = async (automationId: string) => {
    const dumpTab = window.open('', '_blank')
    if (!dumpTab) {
      return
    }

    writeTextToDumpTab({
      dumpTab,
      title: `Automation ${automationId} data`,
      text: 'Loading automation data...'
    })

    const restUrl = process.env.REACT_APP_REST_URL
    if (!restUrl) {
      writeTextToDumpTab({
        dumpTab,
        title: `Automation ${automationId} data`,
        text: 'Unable to load data: REST API URL is not configured.'
      })
      return
    }

    try {
      const lcToken = localStorage.getItem('lcToken')
      const adminToken = localStorage.getItem('adminToken')
      const headers: Record<string, string> = {}

      if (lcToken) {
        headers.Authorization = `Bearer ${lcToken}`
      }

      if (adminToken) {
        headers.admin = adminToken
      }

      const response = await fetch(
        `${restUrl}automation/${automationId}/data`,
        {
          headers
        }
      )

      if (!response.ok) {
        const details = await response.text()
        writeTextToDumpTab({
          dumpTab,
          title: `Automation ${automationId} data`,
          text: `Unable to load data (${response.status}). ${details}`
        })
        return
      }

      const dump = await response.json()
      writeTextToDumpTab({
        dumpTab,
        title: `Automation ${automationId} data`,
        text: JSON.stringify(dump, null, 2)
      })
    } catch (error) {
      console.error('Failed to load automation dump', error)
      const message =
        error instanceof Error ? error.message : String(error)
      writeTextToDumpTab({
        dumpTab,
        title: `Automation ${automationId} data`,
        text: `Unable to load automation data.\n\n${message}`
      })
    }
  }

  return (
    <Container>
      <Title>Automation</Title>

      <Content>
        <StatusContainer>
          <StatusTitle>Status</StatusTitle>
          <Status>{formatStatus(status)}</Status>
          {errors.filter(error => error.module).length > 0 && (
            <MiniStatus>
              {Array.from(
                new Map(
                  errors.map(error => [error.module, error])
                ).values()
              )
                .map(error => error.module)
                .join(', ')}{' '}
              might not be completed
            </MiniStatus>
          )}
        </StatusContainer>

        <Flex columnGap={12}>
          {controls.map(({ Icon, action, statuses }, index) =>
            !statuses || statuses?.includes(status) ? (
              <ControlButton key={index} onClick={action}>
                <Icon />
              </ControlButton>
            ) : null
          )}
        </Flex>
      </Content>

      {isExpanded && (
        <HistoryContainer>
          <Text text="History" weight={600} size={16} />
          {isProjectAutomationsLoading ? (
            <Text text="Loading automation history..." size={12} />
          ) : automationHistory.length === 0 ? (
            <Text text="No automation runs yet" size={12} />
          ) : (
            automationHistory.map(automation => (
              <AutomationItemRow key={automation.automationId}>
                <Flex flex={1} alignItems="center">
                  <Text
                    text={formatHistoryDate(automation.createdAt)}
                    weight={600}
                  />
                </Flex>

                <Flex flex={1}>
                  <Text
                    text={formatEntities(automation.entities)}
                    size={12}
                  />
                </Flex>

                <Flex flex={1} justifyContent="flex-end">
                  <SeeMetadataButton
                    onClick={() =>
                      handleSeeDataClick(automation.automationId)
                    }
                  >
                    See data
                  </SeeMetadataButton>
                </Flex>
              </AutomationItemRow>
            ))
          )}
        </HistoryContainer>
      )}

      <RestartModal
        isOpen={isRestartModalOpen}
        closeModal={() => setIsRestartModalOpen(false)}
        projectId={projectId}
        groupId={authGroupId!}
        coverType={projectMeta?.coverType as CoverTypes}
      />

      <ChevronButton onClick={() => setIsExpanded(!isExpanded)}>
        <ChevronIcon isExpanded={isExpanded} />
      </ChevronButton>
    </Container>
  )
}

export default AutomationStatusBar
